import type {
  PumsemBlock,
  PumsemBlockType,
  PumsemListItem,
} from '../model/pumsem.type';

/**
 * 본문 블록 트리를 다루는 순수 함수들.
 *
 * 편집기가 상태를 직접 주무르지 않고 여기를 지난다 — 중첩 목록 안의 블록을
 * 옮기고 지우는 코드는 화면 컴포넌트 안에 있으면 반드시 어긋난다.
 * **경로(index 배열)** 로 위치를 가리킨다: `[1, 0, 2]` 는 "1번 블록의 0번
 * 항목의 2번 자식".
 */
export type BlockPath = number[];

export const BLOCK_LABEL: Record<PumsemBlockType, string> = {
  paragraph: '문단',
  list: '번호목록',
  table: '표',
  figure: '그림',
  formula: '산식',
  notes: '[주]',
};

/**
 * 발행본에서 가장 흔한 열 구성. 대부분의 품 표가 `구분 | 단위 | 수량` 이고,
 * 규격 축이 있는 표만 그 오른쪽에 열이 더 붙는다.
 */
export function emptyBlock(type: PumsemBlockType): PumsemBlock {
  switch (type) {
    case 'paragraph':
      return { type: 'paragraph', text: '' };
    case 'list':
      return {
        type: 'list',
        style: 'decimal',
        items: [{ marker: '1', text: '', children: [] }],
      };
    case 'table':
      return {
        type: 'table',
        table: {
          caption: '',
          valueUnit: null,
          columns: [
            { key: 'c1', label: '단위', group: null },
            { key: 'c2', label: '수량', group: null },
          ],
          rows: [],
        },
      };
    case 'figure':
      return { type: 'figure', src: '', alt: '', caption: null };
    case 'formula':
      return { type: 'formula', text: '', legend: [] };
    case 'notes':
      return { type: 'notes', notes: [] };
  }
}

/** 본문에 배치한 [주]를 문서 순서로. 항목 `notes` 의 앞쪽과 같다. */
export function placedNotes(blocks: PumsemBlock[]): string[] {
  return blocks.flatMap((block) => {
    if (block.type === 'notes') return block.notes;
    if (block.type === 'list') {
      return block.items.flatMap((item) => placedNotes(item.children));
    }
    return [];
  });
}

/**
 * 항목 `notes` 중 본문에 배치하지 않은 것. 서버가 `[배치한 것, 배치 안 한 것]`
 * 순서로 조립하므로 앞에서 배치한 개수만큼 떼면 된다.
 */
export function unplacedNotes(notes: string[], body: PumsemBlock[]): string[] {
  return notes.slice(placedNotes(body).length);
}

/** 다음 번호를 짐작한다. 마지막 항목이 숫자면 +1, 한글이면 가나다 다음 글자. */
export function nextMarker(items: PumsemListItem[]): string {
  const last = items[items.length - 1]?.marker;
  if (!last) return '1';

  const numeric = Number(last);
  if (Number.isInteger(numeric)) return String(numeric + 1);

  const HANGUL_ORDER = '가나다라마바사아자차카타파하';
  const at = HANGUL_ORDER.indexOf(last);
  if (at >= 0 && at < HANGUL_ORDER.length - 1) return HANGUL_ORDER[at + 1];

  const CIRCLED = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮';
  const circled = CIRCLED.indexOf(last);
  if (circled >= 0 && circled < CIRCLED.length - 1) return CIRCLED[circled + 1];

  return '';
}

/** 경로가 가리키는 배열을 통째로 바꿔 새 트리를 만든다. 원본은 건드리지 않는다. */
function updateList(
  blocks: PumsemBlock[],
  path: BlockPath,
  transform: (list: PumsemBlock[]) => PumsemBlock[],
): PumsemBlock[] {
  if (path.length === 0) return transform(blocks);

  const [blockIndex, itemIndex, ...rest] = path;
  return blocks.map((block, index) => {
    if (index !== blockIndex || block.type !== 'list') return block;
    return {
      ...block,
      items: block.items.map((item, i) =>
        i !== itemIndex
          ? item
          : { ...item, children: updateList(item.children, rest, transform) },
      ),
    };
  });
}

/** 경로가 가리키는 배열을 읽는다. 없으면 빈 배열. */
export function listAt(blocks: PumsemBlock[], path: BlockPath): PumsemBlock[] {
  if (path.length === 0) return blocks;
  const [blockIndex, itemIndex, ...rest] = path;
  const block = blocks[blockIndex];
  if (block?.type !== 'list') return [];
  return listAt(block.items[itemIndex]?.children ?? [], rest);
}

export function insertBlock(
  blocks: PumsemBlock[],
  path: BlockPath,
  block: PumsemBlock,
): PumsemBlock[] {
  return updateList(blocks, path, (list) => [...list, block]);
}

export function replaceBlock(
  blocks: PumsemBlock[],
  path: BlockPath,
  index: number,
  block: PumsemBlock,
): PumsemBlock[] {
  return updateList(blocks, path, (list) =>
    list.map((current, i) => (i === index ? block : current)),
  );
}

export function removeBlock(
  blocks: PumsemBlock[],
  path: BlockPath,
  index: number,
): PumsemBlock[] {
  return updateList(blocks, path, (list) =>
    list.filter((_, i) => i !== index),
  );
}

/** 위/아래 한 칸. 끝에서 더 밀면 그대로 둔다 — 순환하면 사용자가 자리를 잃는다. */
export function moveBlock(
  blocks: PumsemBlock[],
  path: BlockPath,
  index: number,
  delta: number,
): PumsemBlock[] {
  return updateList(blocks, path, (list) => {
    const to = index + delta;
    if (to < 0 || to >= list.length) return list;
    const next = [...list];
    const [picked] = next.splice(index, 1);
    next.splice(to, 0, picked);
    return next;
  });
}

/** 중첩 포함 총 블록 수. 서버 상한(400)에 걸리기 전에 화면이 먼저 말해준다. */
export function countBlocks(blocks: PumsemBlock[]): number {
  return blocks.reduce((sum, block) => {
    if (block.type !== 'list') return sum + 1;
    return (
      sum +
      1 +
      block.items.reduce((inner, item) => inner + countBlocks(item.children), 0)
    );
  }, 0);
}
